import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import api from '../../api';
import { theme } from '../../theme';
import { SubathonRules, SubathonSettings } from '../../types/subathon';
import {
  Button,
  ErrorText,
  Input,
  Label,
  Muted,
  PanelGrid,
  Surface,
  SurfaceBody,
  SurfaceHeader,
  SurfaceTitle,
  Toast,
} from '../ui';

interface Props {
  onSaved: () => void;
}

const Rule = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 0.6rem;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
`;

const RuleName = styled.div`
  font-size: 0.88rem;
  color: #fff;

  small {
    display: block;
    color: ${theme.colors.textMuted};
    font-size: 0.74rem;
    margin-top: 0.1rem;
  }
`;

const RuleInputs = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  color: ${theme.colors.textMuted};

  input {
    width: 76px;
    text-align: center;
  }
`;

const Check = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  font-size: 0.85rem;
  color: #fff;
  cursor: pointer;
  padding: 0.35rem 0;

  input {
    accent-color: ${theme.colors.pink};
    width: 16px;
    height: 16px;
    margin-top: 0.15rem;
  }

  span small {
    display: block;
    color: ${theme.colors.textMuted};
    font-size: 0.76rem;
    margin-top: 0.1rem;
  }
`;

const Mono = styled.code`
  display: block;
  font-family: 'Consolas', monospace;
  font-size: 0.76rem;
  padding: 0.5rem 0.6rem;
  border-radius: 8px;
  background: rgba(10, 7, 22, 0.7);
  border: 1px solid rgba(255, 20, 147, 0.18);
  word-break: break-all;
  color: #fff;
`;

const Status = styled.span<{ $ok?: boolean }>`
  font-size: 0.76rem;
  font-weight: 700;
  color: ${({ $ok }) => ($ok ? '#9dffb0' : theme.colors.textMuted)};
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

/** Cada regla se edita en minutos, que es como piensa Euge, y se guarda en segundos. */
type RuleField = {
  label: string;
  hint?: string;
  unitKey?: keyof SubathonRules;
  unitSuffix?: string;
  secondsKey: keyof SubathonRules;
  unitStep?: number;
};

const TWITCH_RULES: RuleField[] = [
  { label: 'Follow nuevo', secondsKey: 'followSeconds' },
  { label: 'Raid', secondsKey: 'raidSeconds', hint: 'Fijo por raid, no por espectador' },
  { label: 'Bits', secondsKey: 'bitsSeconds', unitKey: 'bitsUnit', unitSuffix: 'bits' },
  { label: 'Sub tier 1', secondsKey: 'subTier1Seconds' },
  { label: 'Sub tier 2', secondsKey: 'subTier2Seconds' },
  { label: 'Sub tier 3', secondsKey: 'subTier3Seconds' },
];

const EXTERNAL_RULES: RuleField[] = [
  {
    label: 'Cafecito',
    secondsKey: 'cafecitoSeconds',
    unitKey: 'cafecitoUnit',
    unitSuffix: 'cafecitos',
    hint: 'Se carga a mano: Cafecito no tiene API',
  },
  {
    label: 'Ko-fi',
    secondsKey: 'coffeeSeconds',
    unitKey: 'coffeeUnit',
    unitSuffix: 'cafés',
  },
  {
    label: 'Propina (StreamElements)',
    secondsKey: 'tipSeconds',
    unitKey: 'tipUnit',
    unitSuffix: 'de plata',
    unitStep: 0.5,
  },
  {
    label: 'Cofre de Streamloots',
    secondsKey: 'chestSeconds',
    hint: 'Se carga a mano: Streamloots no avisa por API',
  },
];

function toMinutes(seconds: number): string {
  if (!seconds) return '0';
  const minutes = seconds / 60;
  return String(Number.isInteger(minutes) ? minutes : Math.round(minutes * 100) / 100);
}

const SubathonRulesTab: React.FC<Props> = ({ onSaved }) => {
  const [settings, setSettings] = useState<SubathonSettings | null>(null);
  const [rules, setRules] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);

  const [kofiToken, setKofiToken] = useState('');
  const [seJwt, setSeJwt] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get<SubathonSettings>('/subathon/settings');
      setSettings(data);
      const next: Record<string, string> = {};
      Object.entries(data.rules).forEach(([key, value]) => {
        if (typeof value !== 'number') return;
        next[key] = key.endsWith('Seconds') ? toMinutes(value) : String(value);
      });
      setRules(next);
      setError('');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo cargar la configuración');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const notify = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(''), 2600);
  };

  const patch = async (body: any, message = 'Guardado ✓') => {
    setSaving(true);
    try {
      const { data } = await api.put<SubathonSettings>('/subathon/settings', body);
      setSettings(data);
      setError('');
      notify(message);
      onSaved();
      return true;
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo guardar');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveRules = () => {
    const payload: Record<string, number> = {};
    Object.entries(rules).forEach(([key, raw]) => {
      const value = parseFloat(raw);
      if (!Number.isFinite(value) || value < 0) return;
      payload[key] = key.endsWith('Seconds') ? Math.round(value * 60) : value;
    });
    patch({
      rules: {
        ...payload,
        raidPerViewer: settings?.rules.raidPerViewer,
        primeAsTier1: settings?.rules.primeAsTier1,
        creditWhilePaused: settings?.rules.creditWhilePaused !== false,
      },
    });
  };

  const setRule = (key: string, value: string) => setRules((prev) => ({ ...prev, [key]: value }));

  const testIntegration = async (provider: 'kofi' | 'streamelements') => {
    try {
      const { data } = await api.post(`/subathon/integrations/${provider}/test`);
      if (provider === 'streamelements') {
        notify(data.ok ? 'StreamElements conectado ✓' : `Sin conexión: ${data.status?.lastError || 'revisá el JWT'}`);
      } else {
        notify(
          data.lastEventAt
            ? `Último evento de Ko-fi: ${new Date(data.lastEventAt).toLocaleString()}`
            : 'Todavía no llegó ningún evento de Ko-fi',
        );
      }
      await load();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'No se pudo probar la integración');
    }
  };

  if (!settings) {
    return error ? <ErrorText>{error}</ErrorText> : <Muted>Cargando reglas…</Muted>;
  }

  const it = settings.integrations;

  const renderRule = (field: RuleField) => (
    <Rule key={String(field.secondsKey)}>
      <RuleName>
        {field.label}
        {field.hint && <small>{field.hint}</small>}
      </RuleName>
      <RuleInputs>
        {field.unitKey && (
          <>
            <span>cada</span>
            <Input
              type="number"
              min={field.unitStep ? 0.01 : 1}
              step={field.unitStep || 1}
              value={rules[field.unitKey] ?? ''}
              onChange={(e) => setRule(String(field.unitKey), e.target.value)}
            />
            <span>{field.unitSuffix}</span>
          </>
        )}
        <span>{field.unitKey ? 'suman' : 'suma'}</span>
        <Input
          type="number"
          min={0}
          step={0.5}
          value={rules[field.secondsKey] ?? ''}
          onChange={(e) => setRule(String(field.secondsKey), e.target.value)}
        />
        <span>min</span>
      </RuleInputs>
    </Rule>
  );

  return (
    <>
      {error && <ErrorText>{error}</ErrorText>}
      {toast && <Toast>{toast}</Toast>}

      <PanelGrid $cols="minmax(0, 1fr) minmax(0, 1fr)">
        <Stack>
          <Surface>
            <SurfaceHeader>
              <SurfaceTitle>Cuánto suma cada cosa en Twitch</SurfaceTitle>
            </SurfaceHeader>
            <SurfaceBody>
              {TWITCH_RULES.map(renderRule)}
              <Check style={{ marginTop: '0.6rem' }}>
                <input
                  type="checkbox"
                  checked={!!settings.rules.raidPerViewer}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      rules: { ...settings.rules, raidPerViewer: e.target.checked },
                    })
                  }
                />
                <span>
                  Multiplicar el raid por espectadores
                  <small>Con 10 seg y un raid de 40, suman 400 seg en vez de 10.</small>
                </span>
              </Check>
              <Check>
                <input
                  type="checkbox"
                  checked={settings.rules.primeAsTier1 !== false}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      rules: { ...settings.rules, primeAsTier1: e.target.checked },
                    })
                  }
                />
                <span>
                  Las subs de Prime valen como tier 1
                  <small>Si lo destildás, Prime igual suma tier 1 salvo que la regla esté en 0.</small>
                </span>
              </Check>
            </SurfaceBody>
          </Surface>

          <Surface>
            <SurfaceHeader>
              <SurfaceTitle>Cuánto suma lo de afuera de Twitch</SurfaceTitle>
            </SurfaceHeader>
            <SurfaceBody>
              {EXTERNAL_RULES.map(renderRule)}
              <Rule>
                <RuleName>
                  Precio de un café en Ko-fi
                  <small>Ko-fi avisa el monto, no la cantidad: con esto se traduce.</small>
                </RuleName>
                <RuleInputs>
                  <Input
                    type="number"
                    min={0.01}
                    step={0.5}
                    value={rules.coffeePrice ?? ''}
                    onChange={(e) => setRule('coffeePrice', e.target.value)}
                  />
                </RuleInputs>
              </Rule>
              <Button style={{ marginTop: '0.8rem' }} disabled={saving} onClick={saveRules}>
                Guardar reglas
              </Button>
            </SurfaceBody>
          </Surface>
        </Stack>

        <Stack>
          <Surface>
            <SurfaceHeader>
              <SurfaceTitle>Arranque y tope</SurfaceTitle>
            </SurfaceHeader>
            <SurfaceBody>
              <Rule>
                <RuleName>
                  Minutos de arranque
                  <small>Lo que propone la pestaña Control. En 0 espera el primer aporte.</small>
                </RuleName>
                <RuleInputs>
                  <Input
                    type="number"
                    min={0}
                    value={settings.startMinutes}
                    onChange={(e) =>
                      setSettings({ ...settings, startMinutes: parseInt(e.target.value, 10) || 0 })
                    }
                  />
                  <span>min</span>
                </RuleInputs>
              </Rule>
              <Rule>
                <RuleName>
                  Tope máximo
                  <small>El contador nunca pasa de acá. Lo que sobra queda registrado igual.</small>
                </RuleName>
                <RuleInputs>
                  <Input
                    type="number"
                    min={1}
                    value={settings.capMinutes}
                    onChange={(e) =>
                      setSettings({ ...settings, capMinutes: parseInt(e.target.value, 10) || 1 })
                    }
                  />
                  <span>min</span>
                </RuleInputs>
              </Rule>
              <Check>
                <input
                  type="checkbox"
                  checked={settings.capEnabled}
                  onChange={(e) => setSettings({ ...settings, capEnabled: e.target.checked })}
                />
                <span>Usar el tope máximo</span>
              </Check>
              <Check>
                <input
                  type="checkbox"
                  checked={settings.rules.creditWhilePaused !== false}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      rules: { ...settings.rules, creditWhilePaused: e.target.checked },
                    })
                  }
                />
                <span>
                  En pausa sigue sumando tiempo
                  <small>
                    El reloj queda quieto pero los follows, subs, bits y donaciones se acumulan
                    igual. Si lo destildás, lo que entre durante la pausa no suma nada. Las cargas a
                    mano del panel suman siempre.
                  </small>
                </span>
              </Check>
              <Check>
                <input
                  type="checkbox"
                  checked={settings.soundEnabled}
                  onChange={(e) => setSettings({ ...settings, soundEnabled: e.target.checked })}
                />
                <span>
                  Permitir sonido en los overlays
                  <small>Cada overlay elige después qué sonido usa al llegar a cero.</small>
                </span>
              </Check>
              <Button
                style={{ marginTop: '0.7rem' }}
                disabled={saving}
                onClick={() =>
                  patch({
                    startMinutes: settings.startMinutes,
                    capMinutes: settings.capMinutes,
                    capEnabled: settings.capEnabled,
                    soundEnabled: settings.soundEnabled,
                    rules: {
                      raidPerViewer: settings.rules.raidPerViewer,
                      primeAsTier1: settings.rules.primeAsTier1,
                      creditWhilePaused: settings.rules.creditWhilePaused !== false,
                    },
                  })
                }
              >
                Guardar arranque y tope
              </Button>
            </SurfaceBody>
          </Surface>

          <Surface>
            <SurfaceHeader>
              <SurfaceTitle>Ko-fi</SurfaceTitle>
              <Status $ok={it.kofiEnabled && it.kofiTokenSet}>
                {it.kofiEnabled ? (it.kofiTokenSet ? 'Listo' : 'Falta el token') : 'Apagado'}
              </Status>
            </SurfaceHeader>
            <SurfaceBody>
              <Muted style={{ marginTop: 0 }}>
                Pegá esta URL en ko-fi.com/manage/webhooks y copiá de ahí el Verification Token.
              </Muted>
              <Mono>{it.kofiWebhookUrl}</Mono>
              <Label style={{ marginTop: '0.7rem' }}>Verification Token</Label>
              <Input
                type="password"
                placeholder={it.kofiTokenSet ? '•••••••• (ya guardado)' : 'Pegalo acá'}
                value={kofiToken}
                onChange={(e) => setKofiToken(e.target.value)}
              />
              <Check style={{ marginTop: '0.4rem' }}>
                <input
                  type="checkbox"
                  checked={it.kofiEnabled}
                  onChange={(e) =>
                    patch({ integrations: { kofiEnabled: e.target.checked } }, e.target.checked ? 'Ko-fi activado' : 'Ko-fi apagado')
                  }
                />
                <span>Sumar tiempo con lo que entre por Ko-fi</span>
              </Check>
              <Check>
                <input
                  type="checkbox"
                  checked={it.kofiCountSubscriptions}
                  onChange={(e) => patch({ integrations: { kofiCountSubscriptions: e.target.checked } })}
                />
                <span>
                  Contar también las membresías
                  <small>Si lo destildás, solo suman los pagos sueltos.</small>
                </span>
              </Check>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
                <Button
                  $size="sm"
                  disabled={saving || !kofiToken.trim()}
                  onClick={async () => {
                    const ok = await patch({ integrations: { kofiToken: kofiToken.trim() } }, 'Token de Ko-fi guardado ✓');
                    if (ok) setKofiToken('');
                  }}
                >
                  Guardar token
                </Button>
                <Button $size="sm" $variant="ghost" onClick={() => testIntegration('kofi')}>
                  Ver último evento
                </Button>
              </div>
              {it.kofiLastEventAt && (
                <Muted style={{ marginBottom: 0 }}>
                  Último evento: {new Date(it.kofiLastEventAt).toLocaleString()}
                </Muted>
              )}
            </SurfaceBody>
          </Surface>

          <Surface>
            <SurfaceHeader>
              <SurfaceTitle>StreamElements</SurfaceTitle>
              <Status $ok={it.seStatus?.connected}>
                {it.seEnabled ? (it.seStatus?.connected ? 'Conectado' : 'Sin conexión') : 'Apagado'}
              </Status>
            </SurfaceHeader>
            <SurfaceBody>
              <Muted style={{ marginTop: 0 }}>
                El JWT está en streamelements.com/dashboard/account/channels, con el interruptor
                «Show secrets». Si tenés varias plataformas conectadas, copiá el del canal de
                Twitch. Queda guardado en el servidor y el panel no lo vuelve a mostrar.
              </Muted>
              <Label style={{ marginTop: '0.7rem' }}>JWT Token</Label>
              <Input
                type="password"
                placeholder={it.seJwtSet ? '•••••••• (ya guardado)' : 'Pegalo acá'}
                value={seJwt}
                onChange={(e) => setSeJwt(e.target.value)}
              />
              <Label style={{ marginTop: '0.5rem' }}>Channel ID (opcional)</Label>
              <Input
                value={settings.integrations.seChannelId}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    integrations: { ...settings.integrations, seChannelId: e.target.value },
                  })
                }
              />
              <Check style={{ marginTop: '0.4rem' }}>
                <input
                  type="checkbox"
                  checked={it.seEnabled}
                  onChange={(e) =>
                    patch(
                      { integrations: { seEnabled: e.target.checked } },
                      e.target.checked ? 'StreamElements activado' : 'StreamElements apagado',
                    )
                  }
                />
                <span>Sumar tiempo con las propinas de StreamElements</span>
              </Check>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', flexWrap: 'wrap' }}>
                <Button
                  $size="sm"
                  disabled={saving || (!seJwt.trim() && !settings.integrations.seChannelId)}
                  onClick={async () => {
                    const body: any = { integrations: { seChannelId: settings.integrations.seChannelId } };
                    if (seJwt.trim()) body.integrations.seJwt = seJwt.trim();
                    const ok = await patch(body, 'StreamElements guardado ✓');
                    if (ok) setSeJwt('');
                  }}
                >
                  Guardar y conectar
                </Button>
                <Button $size="sm" $variant="ghost" onClick={() => testIntegration('streamelements')}>
                  Probar conexión
                </Button>
              </div>
              {it.seStatus?.lastError && <Muted style={{ marginBottom: 0 }}>{it.seStatus.lastError}</Muted>}
              {it.seLastEventAt && (
                <Muted style={{ marginBottom: 0 }}>
                  Última propina: {new Date(it.seLastEventAt).toLocaleString()}
                </Muted>
              )}
            </SurfaceBody>
          </Surface>
        </Stack>
      </PanelGrid>
    </>
  );
};

export default SubathonRulesTab;
